import PageHead from '@/components/PageHead';
import { SocialActionCard } from '@/components/SocialActionCard';
import { ExtendProps } from 'app';

import NextImage from 'next/image';
import NextLink from 'next/link';
import dynamic from 'next/dynamic';
import SocialActionLink from '@/components/SocialActionLink';
import { FiExternalLink } from 'react-icons/fi';

const Container = dynamic(() => import('@/components/Container'), {
	loading: () => <p>Loading...</p>,
});

const HeadingOne = dynamic(() => import('@/components/Heading/HeadingOne'), {
	loading: () => <p>Loading...</p>,
});

const PageLayout = dynamic(() => import('@/components/layout/page-layout'), {
	loading: () => <p>Loading...</p>,
});

// import Container from '@/components/Container';
// import HeadingOne from '@/components/Heading/HeadingOne';
// import PageLayout from '@/components/layout/page-layout';

export default function Page() {
	return (
		<>
			<PageHead
				page={{
					title: 'Instituto Deixe Vivo | Saúde Renal: cuidado e prevenção',
					keywords:
						'OSC, Instituto, Deixe Vivo, Doação de órgãos e tecidos, transplantes, renal, tecidos, história, trajetória',
					colorScheme: 'light dark',
					description: 'Instituto Deixe Vivo',
					robots: 'follow, index',
				}}
				og={{
					description:
						'Instituto Deixe Vivo | Saúde Renal: cuidado e prevenção',
					site_name: 'Instituto Deixe Vivo | Saúde Renal: cuidado e prevenção',
					title: 'Instituto Deixe Vivo | Saúde Renal: cuidado e prevenção',
					type: 'website',
					url: 'https://deixevivo.org.br',
					image: {
						alt: 'Logo do Instituto, ao lado esquerdo da imagem um rosto feliz e ao lado direito texto que diz Instituto Deixe Vivo',
						fbAdmins: 'instituto.deixevivo',
						src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681919729/InstitutoDeixeVivo/logos/logo-deixe-vivo-1200x1200.jpg',
						type: 'image/jpg',
						width: '1200',
						height: '1200',
					},
				}}
				twitter={{
					card: 'summary',
					description:
						'Instituto Deixe Vivo | Saúde Renal: cuidado e prevenção',
					site: 'Instituto Deixe Vivo | Saúde Renal: cuidado e prevenção',
					title: 'Instituto Deixe Vivo | Saúde Renal: cuidado e prevenção',
					url: 'https://deixevivo.org.br',
					image: {
						alt: 'Logo do Instituto, ao lado esquerdo da imagem um rosto feliz e ao lado direito texto que diz Instituto Deixe Vivo',
						src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681919729/InstitutoDeixeVivo/logos/logo-deixe-vivo-1200x1200.jpg',
					},
				}}
			/>
			<PageLayout>
				<Container className="py-8">
					<div className="relative">
						<HeadingOne horizontalRow>
							Saúde Renal: cuidado e prevenção
						</HeadingOne>
						<NextImage
							className="float-right mt-4"
							width={192}
							height={192}
							src="https://res.cloudinary.com/oliveiradev/image/upload/v1688396073/InstitutoDeixeVivo/logos/logo-saude-renal.png"
							alt="Saúde Renal: cuidado e prevenção"
						/>
					</div>

					<section className="mt-4">
						<p tabIndex={0}>
							Na nossa frente de saúde, atuamos muito com&nbsp;
							<em className="not-italic">
								<strong>cuidado e prevenção da doença renal</strong>
							</em>
							. Temos ações, projetos e conteúdos para quem já convive com o
							diagnóstico, em todos os estágios (tratamento conservador,
							hemodiálise e transplante). Também nos preocupamos quanto à
							prevenção da saúde dos rins, uma vez que diversas doenças renais
							podem ser prevenidas e tratadas, basta apenas bons hábitos para se
							ter uma boa qualidade de vida!
						</p>

						<ul
							role="list"
							className="mt-8 w-full flex flex-wrap justify-center py-8 gap-4 xl:justify-between items-center"
						>
							<li tabIndex={0}>
								<SocialActionCard
									context="Ação Presencial"
									title="Saúde Renal: Cuidado e Prevenção"
									description="Levamos a
										conscientização da importância dos cuidados com a saúde dos
										rins por meio de palestras dinâmicas, ação de aferição de
										pressão arterial, teste de glicemia, teste de creatinina,
										distribuição de Garrafinhas/Squeezes, bottoms e cards de
										incentivo."
									Link={
										<SocialActionLink>
											<NextLink
												className="flex justify-center w-full h-full cursor-pointer p-3 "
												target="_blank"
												href="mailto:eventos@deixevivo.org.br"
											>
												<span>Saiba mais!</span>
												<FiExternalLink className="ml-1" />
											</NextLink>
										</SocialActionLink>
									}
								/>
							</li>

							<li tabIndex={0}>
								<SocialActionCard
									context="Evento online"
									title="Cuidado e prevenção da saúde renal no
									dia-a-dia"
									description="Fornecemos palestras dinâmicas personalizadas podendo ter a
									presença de nefrologistas, nutricionistas, nutrólogos, prática
									de Yoga"
									Link={
										<SocialActionLink>
											<NextLink
												className="flex justify-center w-full h-full cursor-pointer p-3 "
												target="_blank"
												href="mailto:eventos@deixevivo.org.br"
											>
												<span>Saiba mais!</span>
												<FiExternalLink className="ml-1" />
											</NextLink>
										</SocialActionLink>
									}
								/>
							</li>

							<li tabIndex={0}>
								<SocialActionCard
									context="Ação Social"
									title="Deixe Vivo Doa"
									description="Arrecadamos verba para compra
									de cestas básicas, itens pessoais e outros para entrega à
									pacientes em hemodiálise de clínicas e hospitais."
									Link={
										<SocialActionLink>
											<NextLink
												className="flex justify-center w-full h-full cursor-pointer p-3 "
												target="_blank"
												href="mailto:doacao@deixevivo.org.br"
											>
												<span>Saiba mais!</span>
												<FiExternalLink className="ml-1" />
											</NextLink>
										</SocialActionLink>
									}
								/>
							</li>

							<li tabIndex={0}>
								<SocialActionCard
									context="Ação Dia das Crianças"
									title="Deixe Vivo Doa Kids"
									description="Todos os anos fazemos a campanha de arrecadação de verba para compra de
									brinquedos, cesta básica e panetones para ajudar crianças, e
									suas famílias, que estão no tratamento de hemodiálise."
									Link={
										<SocialActionLink>
											<NextLink
												className="flex justify-center w-full h-full cursor-pointer p-3 "
												target="_blank"
												href="mailto:doacao@deixevivo.org.br"
											>
												<span>Saiba mais!</span>
												<FiExternalLink className="ml-1" />
											</NextLink>
										</SocialActionLink>
									}
								/>
							</li>
						</ul>
					</section>

					<section className="mt-8 flex flex-col lg:flex-row items-center">
						<NextImage
							className=""
							width={192}
							height={192}
							src="https://res.cloudinary.com/oliveiradev/image/upload/v1688396073/InstitutoDeixeVivo/logos/logo-dia-mundial-do-rim.png"
							alt="Educar para Doar: doação de órgãos"
						/>
						<div>
							<h2 className="font-bold" tabIndex={0}>
								Data Especial
							</h2>
							<p tabIndex={0}>
								O Dia Mundial do Rim acontece na segunda quinta-feira do mês de
								Março e cada ano tem um tema diferente proposto pela Sociedade
								Brasileira de Nefrologia. O Instituto Deixe Vivo sempre programa
								conteúdos, lives, ações e projetos especiais para a data, sempre
								de acordo com o tema proposto.
							</p>
						</div>
					</section>
				</Container>
			</PageLayout>
		</>
	);
}
